import { MoreButton } from "@/src/common/MoreButton";
import {
  useOpenCamera,
  useOpenCameraWithPromise,
  useOpenImagePicker,
  useOpenImagePickerWithPromise,
} from "@/src/components/mobile/useImageActionSheet";

import { FeatureFlags } from "@/src/config";
import { RichDescriptionInputButton } from "@/src/components/RichDescriptionInput";
import { useHasFeatureFlag } from "@/src/useHasFeatureFlag";
import { Box, useUploadImage } from "arkh";
import { useCallback } from "react";
import { useIntl } from "react-intl";
import { Platform } from "react-native";

export function TodoAttachmentControlls(props: {
  onImageAdd: (media: { uri: string; width: number; height: number }) => void;
  highlightCamera?: boolean;

  onRequestLinkProduct: () => void;
}) {
  const { openCamera } = useOpenCamera({
    onAddImage: (uri, media) => props.onImageAdd(media),
    options: {
      mediaTypes: ["images" as const],
      allowsEditing: false,
      quality: 1,
    },
  });
  const { openImagePicker } = useOpenImagePicker({
    onAddImage: (uri, media) => props.onImageAdd(media),
    options: {
      mediaTypes: ["images" as const],
      allowsEditing: false,
      quality: 1,
    },
  });
  return (
    <TodoAttachmentControllsComponent
      highlightCamera={props.highlightCamera}
      onRequestLinkProduct={props.onRequestLinkProduct}
      onRequestImagePicker={openImagePicker}
      onRequestCamera={openCamera}
    />
  );
}

export function TodoAttachmentControllsWithPrepare(props: {
  highlightCamera?: boolean;
  onPrepareUploadLink: () => Promise<{ imageId: string; uploadURL: string }>;

  onRequestLinkProduct: () => void;
}) {
  const { prepareForImageUpload, uploadImage } = useUploadImage();
  const onPrepareUploadLink = props.onPrepareUploadLink;

  const openCamera = useOpenCameraWithPromise({
    options: {
      mediaTypes: ["images" as const],
      allowsEditing: false,
      quality: 1,
    },
  });
  const openImagePicker = useOpenImagePickerWithPromise({
    options: {
      mediaTypes: ["images" as const],
      allowsEditing: false,
      quality: 1,
    },
  });
  const onMediaAddAction = useCallback(
    async (media: {
      uri: string;
      width: number;
      height: number;
      imageId: string;
      uploadURL: string;
    }) => {
      const uploadId = await prepareForImageUpload({
        width: media.width,
        height: media.height,
        localImageURI: media.uri,
        imageId: media.imageId,
      });
      uploadImage({
        imageId: media.imageId,
        uploadURL: media.uploadURL,
        uploadId,
      });
    },
    [prepareForImageUpload, uploadImage]
  );
  const openCameraAction = useCallback(async () => {
    const prepareUploadLinkPromise = onPrepareUploadLink();
    const cameraResponse = await openCamera();
    if (!cameraResponse) {
      return;
    }
    const { imageId, uploadURL } = await prepareUploadLinkPromise;
    onMediaAddAction({
      uri: cameraResponse.uri,
      width: cameraResponse.width,
      height: cameraResponse.height,
      imageId,
      uploadURL,
    });
  }, [openCamera, onPrepareUploadLink, onMediaAddAction]);

  const openImagePickerAction = useCallback(async () => {
    const prepareUploadLinkPromise = onPrepareUploadLink();
    const imagePickerResponse = await openImagePicker();
    if (!imagePickerResponse) {
      return;
    }
    const { imageId, uploadURL } = await prepareUploadLinkPromise;
    onMediaAddAction({
      uri: imagePickerResponse.uri,
      width: imagePickerResponse.width,
      height: imagePickerResponse.height,
      imageId,
      uploadURL,
    });
  }, [onPrepareUploadLink, openImagePicker, onMediaAddAction]);

  return (
    <TodoAttachmentControllsComponent
      highlightCamera={props.highlightCamera}
      onRequestLinkProduct={props.onRequestLinkProduct}
      onRequestImagePicker={openImagePickerAction}
      onRequestCamera={openCameraAction}
    />
  );
}

function TodoAttachmentControllsComponent(props: {
  highlightCamera?: boolean;

  onRequestLinkProduct: () => void;
  onRequestImagePicker: () => void;
  onRequestCamera: () => void;
}) {
  const hasProductFeature = useHasFeatureFlag(FeatureFlags.Product);

  const intl = useIntl();

  const hasOnlyImageLibrary = !hasProductFeature && Platform.OS === "web";

  return (
    <>
      <Box
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
        spaceHorizontal="medium"
        spaceBottom="medium"
        gap="small"
      >
        {hasOnlyImageLibrary ? (
          <RichDescriptionInputButton
            type="image"
            onPress={props.onRequestImagePicker}
          />
        ) : (
          <>
            <MoreButton
              items={[
                {
                  label: intl.formatMessage({
                    defaultMessage: "Image library",
                    description: "TodoAttachmentControlls Add image",
                  }),
                  onPress: props.onRequestImagePicker,
                  icon: "image",
                  type: "button",
                },
                ...(hasProductFeature
                  ? [
                      {
                        label: intl.formatMessage({
                          defaultMessage: "Link Product",
                          description: "TodoAttachmentControlls Products",
                        }),
                        onPress: props.onRequestLinkProduct,
                        icon: "product" as const,
                        type: "navigation" as const,
                      },
                    ]
                  : []),
              ]}
            >
              {(onOpen) => (
                <RichDescriptionInputButton
                  type="attachment"
                  onPress={onOpen}
                />
              )}
            </MoreButton>

            {Platform.OS !== "web" ? (
              <RichDescriptionInputButton
                type="camera"
                tintColor={props.highlightCamera ? "text-inverted" : undefined}
                backgroundColor={props.highlightCamera ? "text-cta" : undefined}
                onPress={props.onRequestCamera}
              />
            ) : null}
          </>
        )}
      </Box>
    </>
  );
}
